import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { useLotsList } from "@/features/lots/api/lotsApi";
import { CircleDollarSign, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCostAllocationsList } from "../api/costAllocationsApi";
import { CostAllocationsFilters, type Filters } from "../components/CostAllocationsFilters";
import { CostAllocationsList } from "../components/CostAllocationsList";
import { CostAllocationsListHeader } from "../components/CostAllocationsListHeader";
import { RegisterCostDialog } from "../components/RegisterCostDialog";
import { COST_CATEGORIES, type CostCategory } from "../utils/categories";
import { groupBySourceEvent } from "../utils/groupBy";

function parseCategoryParam(raw: string | null): CostCategory | "" {
  if (!raw) return "";
  return (COST_CATEGORIES as readonly string[]).includes(raw) ? (raw as CostCategory) : "";
}

export function CostAllocationsListPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const canCreate = currentMembership?.role === "admin" || currentMembership?.role === "owner";

  const [searchParams, setSearchParams] = useSearchParams();
  const filters: Filters = {
    lotId: searchParams.get("lot") ?? "",
    // Validar contra allowlist — sin esto un ?category=evil pasa por el cast
    // y pone basura en la URL aunque no afecte resultados.
    category: parseCategoryParam(searchParams.get("category")),
    periodStart: searchParams.get("from") ?? "",
    periodEnd: searchParams.get("to") ?? "",
  };

  function setFilters(next: Filters) {
    const params = new URLSearchParams();
    if (next.lotId) params.set("lot", next.lotId);
    if (next.category) params.set("category", next.category);
    if (next.periodStart) params.set("from", next.periodStart);
    if (next.periodEnd) params.set("to", next.periodEnd);
    setSearchParams(params, { replace: true });
  }

  const lotsQuery = useLotsList(accountId);
  const costsQuery = useCostAllocationsList(accountId, {
    lotId: filters.lotId || undefined,
    periodStart: filters.periodStart || undefined,
    periodEnd: filters.periodEnd || undefined,
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const lots = lotsQuery.data?.lots ?? [];

  // Filtrar categoría client-side (TODO: server-side filter cuando lista
  // supere ~1000 costos por cuenta).
  const grouped = useMemo(() => {
    const rows = costsQuery.data?.costAllocations ?? [];
    const filtered =
      filters.category === "" ? rows : rows.filter((r) => r.category === filters.category);
    return groupBySourceEvent(filtered);
  }, [costsQuery.data, filters.category]);

  const isLoading = costsQuery.isLoading || lotsQuery.isLoading;

  return (
    <>
      <title>Costos · HATO</title>

      {isLoading ? (
        <>
          <CostAllocationsListHeader count={null} onCreate={() => setDialogOpen(true)} />
          <ListSkeleton />
        </>
      ) : costsQuery.isError ? (
        <>
          <CostAllocationsListHeader count={null} onCreate={() => setDialogOpen(true)} />
          <div className="mt-12 flex flex-col items-center gap-4 text-center">
            <p className="text-app-ink-2 text-sm">No se pudieron cargar los costos.</p>
            <button
              type="button"
              onClick={() => void costsQuery.refetch()}
              className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
            >
              Reintentar
            </button>
          </div>
        </>
      ) : (
        <>
          <CostAllocationsListHeader count={grouped.length} onCreate={() => setDialogOpen(true)} />
          <div className="mt-4 flex flex-col gap-4">
            <CostAllocationsFilters filters={filters} onChange={setFilters} lots={lots} />
            {grouped.length === 0 ? (
              <EmptyState
                onCreate={canCreate ? () => setDialogOpen(true) : null}
                hasFilters={
                  filters.lotId !== "" ||
                  filters.category !== "" ||
                  filters.periodStart !== "" ||
                  filters.periodEnd !== ""
                }
              />
            ) : (
              <CostAllocationsList
                costs={grouped}
                lots={lots}
                lotsLoading={lotsQuery.isPending}
                lotsError={lotsQuery.isError}
              />
            )}
          </div>
        </>
      )}

      {canCreate && accountId && (
        <RegisterCostDialog accountId={accountId} open={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </>
  );
}

function ListSkeleton() {
  return (
    <div className="mt-4 space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={i}
          className="h-16 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
    </div>
  );
}

function EmptyState({
  onCreate,
  hasFilters,
}: { onCreate: (() => void) | null; hasFilters: boolean }) {
  if (hasFilters) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-app-border border-dashed bg-app-surface px-6 py-12 text-center">
        <p className="text-app-ink-2 text-sm">No hay costos que coincidan con los filtros.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-app-surface-2 text-app-ink-2">
        <CircleDollarSign size={20} />
      </div>
      <div>
        <h2 className="font-medium text-app-ink-1 text-xl tracking-tight">Sin costos todavía</h2>
        <p className="mt-1 max-w-sm text-app-ink-3 text-sm">
          Los costos se prorratean entre los animales del lote por días de permanencia.
        </p>
      </div>
      {onCreate && (
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-2 rounded-2xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d]"
        >
          <Plus size={16} strokeWidth={2} />
          Registrar primer costo
        </button>
      )}
    </div>
  );
}
